// @ts-nocheck
import { twMerge } from "tailwind-merge"
import { Dialog as HeadlessDialog, Transition } from "@headlessui/react"
import { Fragment, createContext, useContext, useRef, useState } from "react"

type Size = "sm" | "md" | "lg" | "xl"

const dialogContext = createContext<{
  open: boolean
  zoom: boolean
  size: Size
}>({
  open: false,
  zoom: false,
  size: "md",
})

function Dialog({
  children,
  className,
  as = "div",
  open = false,
  onClose,
  staticBackdrop,
  size = "md",
  ...props
}: ExtractProps<typeof HeadlessDialog> & {
  size?: Size
  staticBackdrop?: boolean
}) {
  const focusElement = useRef<HTMLElement | null>(null)
  const [zoom, setZoom] = useState(false)

  return (
    <dialogContext.Provider
      value={{
        open: open,
        zoom: zoom,
        size: size,
      }}
    >
      <Transition appear as={Fragment} show={open}>
        <HeadlessDialog
          as={as}
          onClose={value => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur()
            }

            if (!staticBackdrop) {
              return onClose(value)
            } else {
              setZoom(true)
              setTimeout(() => {
                setZoom(false)
              }, 300)
            }
          }}
          initialFocus={focusElement}
          className={twMerge(["relative z-9999", className])}
          {...props}
        >
          {children}
        </HeadlessDialog>
      </Transition>
    </dialogContext.Provider>
  )
}

Dialog.Panel = ({
  children,
  className,
  as = "div",
  centered,
  ...props
}: ExtractProps<typeof HeadlessDialog.Panel> & {
  size?: Size
  centered?: boolean
}) => {
  const dialog = useContext(dialogContext)
  return (
    <>
      <Transition.Child
        as="div"
        enter="ease-in-out duration-500"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in-out duration-[400ms]"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        className="fixed inset-0 bg-black/60"
        aria-hidden="true"
      />
      <Transition.Child
        as="div"
        enter="ease-in-out duration-500"
        enterFrom="opacity-0 -mt-16"
        enterTo="opacity-100 mt-0"
        leave="ease-in-out duration-[400ms]"
        leaveFrom="opacity-100 pt-16"
        leaveTo="opacity-0 -mt-16 pt-0"
        className="fixed inset-0 overflow-y-auto"
      >
        <div
          className={twMerge(
            centered ? "min-h-screen w-full flex items-center justify-center p-4" : ""
          )}
        >
          <HeadlessDialog.Panel
            as={as}
            className={twMerge([
              "mx-auto bg-white relative rounded-md shadow-md transition-transform dark:bg-darkmode-600",
              dialog.size == "md" && "sm:w-[460px]",
              dialog.size == "sm" && "sm:w-[300px]",
              dialog.size == "lg" && "sm:w-[600px]",
              dialog.size == "xl" && "sm:w-[600px] lg:w-[900px]",
              dialog.zoom && "scale-105",
              className,
            ])}
            {...props}
          >
            {children}
          </HeadlessDialog.Panel>
        </div>
      </Transition.Child>
    </>
  )
}

Dialog.Title = ({
  children,
  className,
  as = "div",
  ...props
}: ExtractProps<typeof HeadlessDialog.Title>) => {
  return (
    <HeadlessDialog.Title
      as={as}
      className={twMerge([
        "flex items-center px-5 py-3 border-b border-slate-200/60 dark:border-darkmode-400",
        className,
      ])}
      {...props}
    >
      {children}
    </HeadlessDialog.Title>
  )
}

Dialog.Description = ({
  children,
  className,
  as = "div",
  ...props
}: ExtractProps<typeof HeadlessDialog.Description>) => {
  return (
    <HeadlessDialog.Description
      as={as}
      className={twMerge(["p-5", className])}
      {...props}
    >
      {children}
    </HeadlessDialog.Description>
  )
}

Dialog.Footer = <C extends React.ElementType = "div">({
  children,
  className,
  as,
  ...props
}: {
  as?: C
} & React.PropsWithChildren &
  React.ComponentPropsWithoutRef<C>) => {
  const Component = as || "div"

  return (
    <Component
      className={twMerge([
        "px-5 py-3 text-right border-t border-slate-200/60 dark:border-darkmode-400",
        className,
      ])}
      {...props}
    >
      {children}
    </Component>
  )
}

export default Dialog
